import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const VirtualOffice = () => {
  const [activeArea, setActiveArea] = useState('reception');

  const officeAreas = [
    {
      id: 'reception',
      name: 'Empfang',
      description: 'Moderne Empfangshalle mit professioneller Atmosphäre',
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=600&fit=crop',
      features: ['24/7 Erreichbarkeit', 'Mehrsprachiger Service', 'Sofortige Terminvergabe'],
      icon: 'Users'
    },
    {
      id: 'consultation',
      name: 'Beratungsräume',
      description: 'Diskrete und komfortable Räume für vertrauliche Gespräche',
      image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&h=600&fit=crop',
      features: ['Schalldichte Räume', 'Modernste Technik', 'Vertrauliche Atmosphäre'],
      icon: 'MessageSquare'
    },
    {
      id: 'library',
      name: 'Rechtsbibliothek',
      description: 'Umfassende Sammlung aktueller Rechtsliteratur und Gesetze',
      image: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=800&h=600&fit=crop',
      features: ['Aktuelle Gesetzestexte', 'Fachzeitschriften', 'Digitale Datenbanken'],
      icon: 'BookOpen'
    },
    {
      id: 'technology',
      name: 'IT-Zentrum',
      description: 'Hochmoderne Technologie für sichere Kommunikation',
      image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=600&fit=crop',
      features: ['Verschlüsselte Kommunikation', 'Cloud-basierte Systeme', 'DSGVO-konform'],
      icon: 'Monitor'
    }
  ];

  const officeStats = [
    {
      icon: 'MapPin',
      label: 'Standort',
      value: 'Berlin',
      description: 'Zentral gelegen am Kurfürstendamm'
    },
    {
      icon: 'Clock',
      label: 'Öffnungszeiten',
      value: '24/7 Service',
      description: 'Rund um die Uhr erreichbar'
    },
    {
      icon: 'Phone',
      label: 'Hotline',
      value: '09001000312',
      description: 'Direkte Verbindung zu unseren Anwälten'
    },
    {
      icon: 'Mail',
      label: 'E-Mail',
      value: 'info@advocall.de',
      description: 'Schnelle schriftliche Kommunikation'
    }
  ];

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Virtueller Rundgang
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Entdecken Sie unsere modernen Büroräume in Berlin und 
            erleben Sie die professionelle Atmosphäre, in der wir arbeiten.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {/* Area Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-professional p-6 border border-slate-200">
              <h3 className="text-xl font-semibold text-text-primary mb-6">
                Bereiche erkunden
              </h3>
              <div className="space-y-3">
                {officeAreas?.map((area) => (
                  <button
                    key={area?.id}
                    onClick={() => setActiveArea(area?.id)}
                    className={`w-full text-left p-4 rounded-lg transition-all duration-300 ${
                      activeArea === area?.id
                        ? 'bg-primary text-white shadow-sm'
                        : 'bg-muted text-text-secondary hover:bg-primary/10 hover:text-primary'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon 
                        name={area?.icon} 
                        size={20} 
                        color={activeArea === area?.id ? 'white' : 'currentColor'} 
                      />
                      <span className="font-medium">{area?.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Area Display */}
          <div className="lg:col-span-2">
            <motion.div
              key={activeArea}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="bg-white rounded-xl shadow-professional overflow-hidden border border-slate-200"
            >
              {/* Image */}
              <div className="relative h-64 lg:h-80 overflow-hidden">
                <Image
                  src={officeAreas?.find(area => area?.id === activeArea)?.image}
                  alt={officeAreas?.find(area => area?.id === activeArea)?.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-2xl font-bold mb-2">
                    {officeAreas?.find(area => area?.id === activeArea)?.name}
                  </h3>
                  <p className="text-white/90">
                    {officeAreas?.find(area => area?.id === activeArea)?.description}
                  </p>
                </div>
              </div>

              {/* Features */}
              <div className="p-6">
                <h4 className="text-lg font-semibold text-text-primary mb-4">
                  Besondere Merkmale
                </h4>
                <div className="grid md:grid-cols-3 gap-4">
                  {officeAreas?.find(area => area?.id === activeArea)?.features?.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Icon name="CheckCircle" size={16} className="text-trust-builder flex-shrink-0" />
                      <span className="text-sm text-text-secondary">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Office Information */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {officeStats?.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-xl p-6 text-center shadow-professional border border-slate-200 card-hover"
            >
              <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg mx-auto mb-4">
                <Icon name={stat?.icon} size={20} className="text-primary" />
              </div>
              <div className="text-sm text-text-secondary mb-1">{stat?.label}</div>
              <div className="text-lg font-semibold text-text-primary mb-2">{stat?.value}</div>
              <div className="text-xs text-text-secondary">{stat?.description}</div>
            </motion.div>
          ))}
        </div>

        {/* Location Map */}
        <div className="bg-white rounded-xl shadow-professional overflow-hidden border border-slate-200">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center space-x-3 mb-4">
              <Icon name="MapPin" size={24} className="text-primary" />
              <h3 className="text-xl font-semibold text-text-primary">
                Unser Standort in Berlin
              </h3>
            </div>
            <p className="text-text-secondary">
              Strategisch gelegen am Kurfürstendamm im Herzen Berlins, 
              bieten wir Ihnen optimale Erreichbarkeit und professionelle Atmosphäre.
            </p>
          </div>
          
          <div className="h-96 relative">
            <iframe
              width="100%"
              height="100%"
              loading="lazy"
              title="AdvoCall Professional - Berlin Office"
              referrerPolicy="no-referrer-when-downgrade"
              src="https://www.google.com/maps?q=52.5027,13.3327&z=14&output=embed"
              className="border-0"
            />
          </div>
          
          <div className="p-6 bg-surface">
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-semibold text-text-primary mb-2">Adresse</h4>
                <p className="text-text-secondary text-sm">
                  ADVOCALL RECHTSANWALTSGESELLSCHAFT MBH<br />
                  Kurfürstendamm 14<br />
                  10719 Berlin
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-text-primary mb-2">Öffentliche Verkehrsmittel</h4>
                <p className="text-text-secondary text-sm">
                  S-Bahn: Zoologischer Garten (3 Min. Fußweg)<br />
                  U-Bahn: Uhlandstraße (2 Min. Fußweg)<br />
                  Bus: Kurfürstendamm/Uhlandstraße
                </p>
              </div>
              <div>
                <h4 className="font-semibild text-text-primary mb-2">Parkmöglichkeiten</h4>
                <p className="text-text-secondary text-sm">
                  Parkhaus Kurfürstendamm<br />
                  Öffentliche Parkplätze<br />
                  Behindertengerechte Stellplätze
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Visit CTA */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-primary to-accent rounded-2xl p-8 text-white">
            <Icon name="Calendar" size={48} color="white" className="mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-4">
              Besuchen Sie uns persönlich
            </h3>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              Vereinbaren Sie einen Termin für ein persönliches Gespräch in unseren 
              modernen Büroräumen oder nutzen Sie unsere 24/7 Telefonberatung.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-8 py-3 bg-white text-primary font-semibold rounded-lg hover:bg-white/90 transition-colors duration-200 cta-button">
                Termin vereinbaren
              </button>
              <button className="px-8 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-primary transition-colors duration-200">
                Jetzt anrufen
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VirtualOffice;